import { localized, tk } from "./i18n"

/**
 * HQ → Supplier finder: find an item at the suppliers (Yupoo catalogues, Weidian shops, WeShop
 * stores) from a photo or a description. The backend reads the stores and keeps a fingerprint of
 * their photos (api/admin/hq/finder); the phone crops and shrinks the photo, searches, and shows the
 * albums that match. Yupoo's photos only load through the HQ Worker (/hq-finder/img/…), which adds the
 * Referer Yupoo insists on; Weidian's and WeShop's load straight from their own photo servers (their
 * photo's "hash" is its address).
 */

export type Strength = "very_likely" | "possible" | "weak"
export type ShopLink = { kind: "taobao" | "tmall" | "weidian" | "1688" | "other"; url: string; item_id: string | null }
export type PhotoRef = { hash: string; handle: string }
/** The rating that applies: the album's own, else its supplier's; "?" = untested (backend ratings.ts). */
export type RatingView = { value: number | null; untested: boolean; source: "album" | "category" | "supplier" | "none" }
/** A rating as the picker holds it: 1–10, "?" (untested), or none. */
export type RatingValue = number | "?" | null
export const toRatingValue = (rating: number | null | undefined, untested: boolean | null | undefined): RatingValue => (rating != null ? rating : untested ? "?" : null)
export const fromRatingValue = (value: RatingValue) => ({ rating: typeof value === "number" ? value : null, untested: value === "?" })
export type Platform = "yupoo" | "weidian" | "weshop"
/** Store kinds, the same in every language. */
export const PLATFORM_NAME: Record<Platform, string> = { yupoo: "Yupoo", weidian: "Weidian", weshop: "WeShop" }

export type AlbumCard = {
  id: string
  supplier: { id: string; name: string; handle: string; platform?: Platform; rating?: number | null; untested?: boolean }
  rating?: RatingView
  /** What it takes without a rating of its own: its category's, else its supplier's. */
  inherited?: RatingView
  title: string
  code: string | null
  price_yuan: number | null
  goods_eur: number | null
  categories: string[]
  link: ShopLink | null
  photo: PhotoRef | null
  cover: PhotoRef | null
  photo_count: number
  gone: boolean
  score: number | null
  strength: Strength | null
  /** An album deleted since the search: only its title and photo then. */
  deleted?: boolean
}

/** Which of a supplier's albums: all, rated on their own, marked «?», or following the supplier. */
export type AlbumRatingFilter = "all" | "own" | "untested" | "inherit"
export type OwnRating = { rating: number | null; untested: boolean }
/** GET /admin/hq/finder/suppliers/:id/albums: a supplier's albums, to find one and rate it. */
export type SupplierAlbums = {
  supplier: { id: string; name: string; platform: Platform; rating: number | null; untested: boolean }
  total: number
  counts: Record<AlbumRatingFilter, number>
  categories: Array<{ id: string; name: string; count: number }>
  albums: Array<AlbumCard & { own_rating: OwnRating }>
}

const ratingKind = (r: OwnRating): Exclude<AlbumRatingFilter, "all"> => (r.rating != null ? "own" : r.untested ? "untested" : "inherit")

/**
 * The list after one album was rated, without asking the server again: its own rating, the rating that
 * applies (as the backend's effectiveRating works it out) and the counts on the tabs. The row stays
 * where it is until the list is next read, so a tap never moves the rows under the thumb.
 */
export function withAlbumRating(data: SupplierAlbums, albumId: string, value: RatingValue): SupplierAlbums {
  const counts = { ...data.counts }
  const own = fromRatingValue(value)
  const s = data.supplier
  const supplierView: RatingView = s.rating != null ? { value: s.rating, untested: false, source: "supplier" } : s.untested ? { value: null, untested: true, source: "supplier" } : { value: null, untested: false, source: "none" }
  const albums = data.albums.map((a) => {
    if (a.id !== albumId) return a
    counts[ratingKind(a.own_rating)] -= 1
    counts[ratingKind(own)] += 1
    // Without its own rating it takes its category's, else the supplier's (what the server sent).
    const view: RatingView = own.rating != null ? { value: own.rating, untested: false, source: "album" } : own.untested ? { value: null, untested: true, source: "album" } : (a.inherited ?? supplierView)
    return { ...a, own_rating: own, rating: view }
  })
  return { ...data, albums, counts }
}

export type FinderChip = { key: string; name: string; kind: "brand" | "type" | "other"; album_count: number; suppliers: number }
export type SupplierStatus = "new" | "importing" | "ready" | "paused" | "error"
export type SupplierProgress = {
  id: string
  name: string
  handle: string
  base_url: string
  platform: Platform
  rating: number | null
  untested: boolean
  has_passcode: boolean
  status: SupplierStatus
  photos_per_album: number
  listed_at: string | null
  listing_due_at: string | null
  /** What can never be finished: photos refused three times, items without a cover, item pages that kept failing. */
  photos_failed: number
  albums_no_cover: number
  pages_failed: number
  /** Items in hidden categories, which are never read. */
  hidden_albums: number
  listing: { done: number; total: number } | null
  last_error: string | null
  /** When reading the store itself started failing; null while it answers. */
  store_failing_since: string | null
  /** What the full readings after the import changed: the last one, and the last 7 days. */
  changes: {
    last: { at: string; seen: number; added: number; gone: number; back: number } | null
    week: { readings: number; added: number; gone: number; back: number }
  }
  albums: number
  pages_read: number
  photos_wanted: number
  photos_indexed: number
  covers_indexed: number
  categories: { total: number; unnamed: number; hidden: number }
}
export type FinderOverview = {
  suppliers: SupplierProgress[]
  chips: FinderChip[]
  vision: { configured: boolean; ok: boolean; model: string | null; error: string | null }
  thresholds: { very_likely: number; possible: number }
}

export type SearchContext = { custom_order?: string; item?: number; from?: string }
export type SortMode = "best" | "match"
export type SearchKind = "photo" | "text" | "code"
export type SearchBody = {
  request_id: string
  sort?: SortMode
  image_b64?: string
  image_url?: string
  q?: string
  query_photo?: string | null
  categories?: string[]
  supplier_ids?: string[]
  context?: SearchContext | null
  limit?: number
}
export type SearchAnswer = {
  id: string
  replay: boolean
  model: string | null
  took_ms: number | null
  results: AlbumCard[]
  kind?: SearchKind
  /** The description as the model read it, when it was turned into English. */
  english?: string | null
  /** Greek words the finder did not know (left out). */
  unknown?: string[]
  /** Brands the description named, which narrowed the search. */
  brands?: string[]
  /** "text_busy": the description matcher was busy and only the words in titles were searched. */
  note?: "text_busy" | "text_off" | null
}

export type SearchRecord = {
  id: string
  created_at: string
  actor: string | null
  source: "upload" | "item_photo" | "code" | "album" | "text" | "link"
  query_photo: string | null
  query_text: string | null
  filters: { categories?: string[]; supplier_ids?: string[]; image_url?: string | null; sort?: SortMode; english?: string | null; brands?: string[] } | null
  context: (SearchContext & { used_for?: string; link?: { kind: "product" | "shop"; source: string; title: string | null; image: string | null; open: string } }) | null
  chosen_album_id?: string | null
  took_ms: number | null
}
export type HistoryRow = SearchRecord & {
  result_count: number
  top: { album_id: string; title: string; hash: string | null; handle: string; score: number } | null
  chosen: { album_id: string; title: string | null; hash: string | null; handle: string | null; at: string | null } | null
}

export type StockOption = { name: string; name_zh: string | null; values: Array<{ value: string; value_zh: string | null }> }
export type StockReading =
  | { source: string; link: string; ok: true; title: string | null; options: StockOption[]; skus: Array<{ values: string[]; stock: number; price_yuan: number | null }>; total_stock: number | null; price_yuan: { low: number; high: number } | null; exact?: boolean; read_at: string }
  | { source: string; link: string; ok: false; reason: "unsupported" | "not_found" | "unavailable"; message: string; read_at: string }

/** The product viewer (backend lib/hq/finder/product.ts): a shop's product page, read for HQ. */
export type ProductOption = { name: string; name_zh: string | null; values: Array<{ value: string; value_zh: string | null; image: string | null }> }
export type ProductShop = { kind: "weidian" | "taobao"; id: string; name: string | null; url: string; logo: string | null }
export type ProductView = {
  source: "weidian" | "taobao" | "tmall" | "1688"
  url: string
  item_id: string
  title: string
  images: string[]
  video: string | null
  price_yuan: { low: number; high: number } | null
  options: ProductOption[]
  skus: Array<{ values: string[]; stock: number; price_yuan: number | null }>
  exact: boolean
  props: Array<{ name: string; name_zh: string | null; value: string }>
  sold: string | null
  shop: ProductShop | null
  read_at: string
}
export type ProductAnswer = { product: ProductView; goods_eur: { low: number; high: number } | null; cached: boolean } | { shop: Pick<ProductShop, "kind" | "id"> }
export type ShopItem = { item_id: string; title: string; image: string | null; price_yuan: number | null; url: string }
export type ShopPage = { ok: true; shop: ProductShop; categories: Array<{ id: string; name: string; count: number | null }> | null; items: ShopItem[]; next: string | null }

/** The product as the stock table reads a shop link. */
export const productReading = (p: ProductView): Extract<StockReading, { ok: true }> => ({
  source: p.source,
  link: p.url,
  ok: true,
  title: p.title,
  options: p.options.map((o) => ({ name: o.name, name_zh: o.name_zh, values: o.values.map((v) => ({ value: v.value, value_zh: v.value_zh })) })),
  skus: p.skus,
  total_stock: p.skus.length ? p.skus.reduce((n, s) => n + s.stock, 0) : null,
  price_yuan: p.price_yuan,
  exact: p.exact,
  read_at: p.read_at,
})

/** Sizes img.alicdn.com serves (checked 16 Oct 2026); 900 is not one of them. */
const ALICDN_SIZES = [60, 120, 160, 200, 240, 300, 400, 460, 600, 640, 720, 800, 960]

/** A smaller copy of a shop photo for thumbnails: Weidian sizes by ?w=, Alibaba's CDN by a suffix. */
export function shopThumb(url: string, width: number): string {
  if (/geilicdn\.com/.test(url)) return `${url.split("?")[0]}?w=${width}`
  if (/alicdn\.com/.test(url) && /\.(jpe?g|png|webp)$/i.test(url)) {
    // Alibaba's CDN answers only its own sizes: anything else (900 × 900) is a 1-pixel placeholder.
    const size = ALICDN_SIZES.find((s) => s >= width)
    return size ? `${url}_${size}x${size}.jpg` : url
  }
  return url
}

export type AlbumDetail = {
  album: {
    id: string
    title: string
    code: string | null
    price_yuan: number | null
    goods_eur: number | null
    rate: number
    description: string | null
    links: ShopLink[]
    buy_link: ShopLink | null
    yupoo_url: string | null
    photo_count: number
    cover_hash: string | null
    gone: boolean
    page_read_at: string | null
    page_error: string | null
    stock: StockReading | null
    stock_read_at: string | null
    categories: Array<{ id: string; name: string; remote_name: string; hidden: boolean }>
    own_rating: { rating: number | null; untested: boolean }
    rating: RatingView
    inherited?: RatingView
  }
  supplier: { id: string; name: string; handle: string; platform?: Platform; rating?: number | null; untested?: boolean } | null
  photos: Array<{ id: string; hash: string; original: string | null; is_cover: boolean }>
  similar: AlbumCard[]
}

export type CategoryRow = {
  id: string
  remote_id: string
  remote_name: string
  name: string | null
  suggestion: string | null
  /** Where the suggestion comes from (backend lib/hq/finder/name-hints.ts); absent from older backends. */
  suggestion_why?: { why: "yours" | "name" | "titles" | "kind" | "words" | "translated" | "code"; of: number | null; total: number | null } | null
  /** The type chip that goes with the suggestion (brand / type / other); absent from older backends. */
  suggestion_kind?: "brand" | "type" | "other" | null
  /** The saved name the suggestion should replace: one the old rule filled in by itself («Shoes»). */
  replaces?: string | null
  kind: "brand" | "type" | "other"
  hidden: boolean
  /** Its own rating (1–10 or "?"); none = its albums follow the supplier's. */
  rating: number | null
  untested: boolean
  album_count: number
  sample_covers: string[] | null
  gone: boolean
  url: string
  /** "Every item": the store's whole list, which catches items in no category. Never a search chip. */
  all?: boolean
}
export type SupplierDetail = {
  supplier: {
    id: string
    name: string
    handle: string
    photo_handle: string
    base_url: string
    platform: Platform
    rating: number | null
    untested: boolean
    has_passcode: boolean
    status: SupplierStatus
    photos_per_album: number
    listed_at: string | null
    last_error: string | null
  }
  categories: CategoryRow[]
  known_names: string[]
}
export type SupplierSave = {
  name?: string | null
  rating?: number | null
  untested?: boolean | null
  passcode?: string | null
  photos_per_album?: number | null
  categories?: Array<{ id: string; name?: string | null; kind?: CategoryRow["kind"]; hidden?: boolean; rating?: number | null; untested?: boolean }>
  start?: boolean
}

// ── Photos ──────────────────────────────────────────────────────────────────────────────────────

const BOX = { small: 500, medium: 800, big: 1200 }

/**
 * A supplier's photo at a size: small for grids, medium, big. Yupoo through the HQ Worker (382×500,
 * 612×800, 826×1080); Weidian's and WeShop's straight from their photo servers, sized there.
 */
export function photoSrc(ref: PhotoRef, size: "small" | "medium" | "big" = "small") {
  if (!/^https:\/\//.test(ref.hash)) return `/hq-finder/img/${ref.handle}/${ref.hash}/${size}`
  const host = new URL(ref.hash).hostname
  if (host === "si.geilicdn.com") return `${ref.hash}?w=${BOX[size]}`
  if (host.endsWith("szwego.com")) return `${ref.hash}?imageMogr2/auto-orient/thumbnail/${BOX[size]}x${BOX[size]}/format/jpg/quality/90`
  return ref.hash
}
/** The full-size original, where its name is one the Worker accepts; else the biggest standard size. */
export const originalSrc = (ref: PhotoRef, original: string | null) =>
  /^https:\/\//.test(ref.hash)
    ? ref.hash
    : original && /^[0-9a-f]{6,16}\.(jpe?g|png|webp|gif)$/.test(original)
      ? `/hq-finder/img/${ref.handle}/${ref.hash}/${original}`
      : photoSrc(ref, "big")
/** A photo an owner searched with (kept privately for History). */
export const queryPhotoSrc = (id: string) => `/hq-finder/query/${id}.jpg`

/** The longest side the search photo is shrunk to on the phone: plenty for the model (224 px), small to send. */
export const QUERY_MAX = 512

// ── Crop ────────────────────────────────────────────────────────────────────────────────────────

/** A crop as fractions of the photo (0–1), so it survives the photo being drawn at any size. */
export type Crop = { x: number; y: number; w: number; h: number }
export const FULL: Crop = { x: 0, y: 0, w: 1, h: 1 }
export type Handle = "nw" | "ne" | "sw" | "se" | "move"
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))

/** Moves a corner (or the whole box) by dx, dy (fractions), keeping it inside the photo and at least `min` wide/tall. */
export function dragCrop(crop: Crop, handle: Handle, dx: number, dy: number, min = 0.1): Crop {
  if (handle === "move") return { ...crop, x: clamp(crop.x + dx, 0, 1 - crop.w), y: clamp(crop.y + dy, 0, 1 - crop.h) }
  let left = crop.x
  let top = crop.y
  let right = crop.x + crop.w
  let bottom = crop.y + crop.h
  if (handle === "nw" || handle === "sw") left = clamp(left + dx, 0, right - min)
  if (handle === "ne" || handle === "se") right = clamp(right + dx, left + min, 1)
  if (handle === "nw" || handle === "ne") top = clamp(top + dy, 0, bottom - min)
  if (handle === "sw" || handle === "se") bottom = clamp(bottom + dy, top + min, 1)
  return { x: left, y: top, w: right - left, h: bottom - top }
}

export const isFull = (crop: Crop) => crop.x < 0.005 && crop.y < 0.005 && crop.w > 0.99 && crop.h > 0.99

/** The crop in the photo's pixels, and the size to draw it at so its longest side is at most `max`. */
export function cropPixels(crop: Crop, width: number, height: number, max = QUERY_MAX) {
  const sx = Math.round(crop.x * width)
  const sy = Math.round(crop.y * height)
  const sw = Math.max(1, Math.round(crop.w * width))
  const sh = Math.max(1, Math.round(crop.h * height))
  const scale = Math.min(1, max / Math.max(sw, sh))
  return { sx, sy, sw, sh, dw: Math.max(1, Math.round(sw * scale)), dh: Math.max(1, Math.round(sh * scale)) }
}

// ── Words ───────────────────────────────────────────────────────────────────────────────────────

export const STRENGTH_LABEL: Record<Strength, string> = { very_likely: tk("Very likely"), possible: tk("Possible"), weak: tk("Weak match") }
/** Shop names, the same in every language. A link to anything else is called "Link" (translated where shown). */
export const SHOP_NAME: Record<Exclude<ShopLink["kind"], "other">, string> = { taobao: "Taobao", tmall: "Tmall", weidian: "Weidian", "1688": "1688" }

/**
 * The finder's filters (suppliers, categories, the order) START CLEAN every time it opens (owners, 21 Oct
 * 2026: "make it reset every time so we don't have to remove filters by hand"). They used to be kept on the
 * device, so yesterday's brand silently narrowed today's search. The old saved choices are wiped once.
 */
const OLD_FILTER_KEYS = ["hq-finder-suppliers", "hq-finder-chips", "hq-finder-sort"]
try {
  for (const key of OLD_FILTER_KEYS) localStorage.removeItem(key)
} catch {
  // Private browsing: nothing was saved.
}

/** Whether a pasted address is one category's link (so the owner can choose: that category, or the whole store). */
export function isCategoryLink(input: string): boolean {
  let url: URL
  try {
    url = new URL(/^https?:\/\//i.test(input.trim()) ? input.trim() : `https://${input.trim()}`)
  } catch {
    return false
  }
  const host = url.hostname.toLowerCase()
  if (/\.yupoo\.com$/.test(host)) return /^\/categories\/\d+/.test(url.pathname)
  if (/weidian\.com$/.test(host)) return ["c", "cateId", "cateid", "cid", "cate_id", "categoryId"].some((k) => /^\d+$/.test(url.searchParams.get(k) ?? ""))
  return /\/weshop\/store\//i.test(url.pathname) && (["tagId", "tag_id", "tagid", "tagList"].some((k) => url.searchParams.get(k)) || /\/tags?\/\d+/.test(url.pathname + url.hash))
}


/** The option names the server reads from a shop (English already), in the chosen language. */
export const OPTION_LABEL: Record<string, string> = localized(
  { Colour: "Colour", Size: "Size", "Shoe size": "Shoe size", Style: "Style", Variant: "Variant", Model: "Model", Set: "Set", Version: "Version" },
  "stock option"
)
export const optionLabel = (name: string) => OPTION_LABEL[name] ?? name

type ChoiceOption = { name: string; name_zh: string | null; values: Array<{ value: string; value_zh: string | null }> }

/**
 * The options chosen, as the SELLER wrote them on the page («颜色分类:黑色;尺码:XL», Taobao's own
 * form): what goes in the item's Chinese for Larry, so he picks exactly that (16 Oct 2026). A value
 * the page gave only in English (Weidian's translated sizes) stays as it is.
 */
export function originalChoice(options: ChoiceOption[], chosen: string[]): string {
  return options
    .map((o, i) => {
      if (!chosen[i]) return null
      const v = o.values.find((value) => value.value === chosen[i])
      return `${o.name_zh ?? o.name}:${v?.value_zh ?? chosen[i]}`
    })
    .filter(Boolean)
    .join(";")
}

/** The same choice in English for the item's description (Larry reads English there): "Colour Black, Size XL". */
export const englishChoice = (options: ChoiceOption[], chosen: string[]) =>
  options.map((o, i) => (chosen[i] ? `${o.name} ${chosen[i]}` : null)).filter(Boolean).join(", ")

/**
 * An item's description with this choice in it: a choice already written there for the same options
 * (a part like "Colour Black, Size 50/L" between " · ") is replaced, never repeated.
 */
export function withChoice(description: string, options: ChoiceOption[], chosen: string[]): string {
  const choice = englishChoice(options, chosen)
  const isChoice = (part: string) => part.split(", ").every((bit) => options.some((o) => bit.startsWith(`${o.name} `)))
  const kept = description.split(" · ").map((part) => part.trim()).filter((part) => part && !isChoice(part))
  return [...kept, choice].filter(Boolean).join(" · ")
}

// ── Stock ───────────────────────────────────────────────────────────────────────────────────────

const SIZE_NAMES = /size|尺码|尺碼|尺寸|码|碼/i

/**
 * A stock reading as a grid: sizes down the side, the other option (colour) across the top, each
 * cell the stock of that combination. One option = one column. More than two = a plain list.
 */
export type StockGrid =
  | { kind: "grid"; rowTitle: string; colTitle: string | null; rows: string[]; cols: string[]; cell: (row: string, col: string) => { stock: number; price_yuan: number | null } | null }
  | { kind: "list"; lines: Array<{ label: string; stock: number; price_yuan: number | null }> }

export function stockGrid(reading: Extract<StockReading, { ok: true }>): StockGrid {
  const opts = reading.options
  const find = new Map(reading.skus.map((s) => [s.values.join("\u0000"), s]))
  if (opts.length === 1) {
    const rows = opts[0].values.map((v) => v.value)
    return { kind: "grid", rowTitle: opts[0].name, colTitle: null, rows, cols: [""], cell: (row) => find.get(row) ?? null }
  }
  if (opts.length === 2) {
    const sizeAt = SIZE_NAMES.test(opts[0].name_zh ?? opts[0].name) && !SIZE_NAMES.test(opts[1].name_zh ?? opts[1].name) ? 0 : 1
    const other = 1 - sizeAt
    const rows = opts[sizeAt].values.map((v) => v.value)
    const cols = opts[other].values.map((v) => v.value)
    return {
      kind: "grid",
      rowTitle: opts[sizeAt].name,
      colTitle: opts[other].name,
      rows,
      cols,
      cell: (row, col) => {
        const values = sizeAt === 0 ? [row, col] : [col, row]
        return find.get(values.join("\u0000")) ?? null
      },
    }
  }
  return { kind: "list", lines: reading.skus.map((s) => ({ label: s.values.filter(Boolean).join(" · ") || "—", stock: s.stock, price_yuan: s.price_yuan })) }
}

/** "61039-60L" from "Price:269 61039-60L", for an item's description when the owner picks an album. */
export function itemDescription(album: { title: string; code: string | null; categories?: string[] }, category?: string | null) {
  return [category ?? album.categories?.[0] ?? null, album.code ?? album.title].filter(Boolean).join(" ").slice(0, 400)
}
